import { useRef, useState, useCallback, useEffect } from 'react'
import { isFirefox } from 'react-device-detect'
import {
  VB_MODES,
  VB_BLUR_AMOUNT,
  VB_CANVAS_SIZE,
  VB_TARGET_FPS,
  VB_INIT_TIMEOUT_MS
} from 'consts/virtualBackgroundConsts'
import { trackEvent } from 'utils/analyticsUtils'
import { log } from 'utils/logUtils'

// Load MediaPipe from CDN to bypass webpack bundling (CRA 4.x babel can't handle modern syntax in the npm package)
const MEDIAPIPE_CDN_VERSION = '0.10.22-rc.20250304'
const MEDIAPIPE_CDN_URL = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${MEDIAPIPE_CDN_VERSION}`
const WASM_CDN_URL = `${MEDIAPIPE_CDN_URL}/wasm`

const getLocalModelUrl = () => {
  const publicUrl = process.env.PUBLIC_URL ?? ''
  const modelPath = `${publicUrl}/models/selfie_segmenter.tflite`

  if (typeof window === 'undefined') {
    return modelPath
  }

  try {
    return new URL(modelPath, `${window.location.origin}/`).toString()
  } catch (err) {
    return modelPath
  }
}

const FALLBACK_MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite'

// Cache for loaded MediaPipe module
let mediaPipeModulePromise = null

const drawVideoCover = (ctx, videoElement, destWidth, destHeight) => {
  const srcWidth = videoElement.videoWidth
  const srcHeight = videoElement.videoHeight

  if (!srcWidth || !srcHeight) return

  const destAspect = destWidth / destHeight
  const srcAspect = srcWidth / srcHeight

  let sx = 0
  let sy = 0
  let sWidth = srcWidth
  let sHeight = srcHeight

  if (srcAspect > destAspect) {
    sWidth = srcHeight * destAspect
    sx = (srcWidth - sWidth) / 2
  } else if (srcAspect < destAspect) {
    sHeight = srcWidth / destAspect
    sy = (srcHeight - sHeight) / 2
  }

  ctx.drawImage(videoElement, sx, sy, sWidth, sHeight, 0, 0, destWidth, destHeight)
}

// The browser's own import(), kept out of the bundler's reach. Vite would otherwise wrap the import in its preload
// helper, whose failure fires vite:preloadError and reloads the page (see chunkLoadUtils), and the legacy build would
// turn it into a SystemJS import, which cannot load a plain ES module from a CDN. A CDN failure must stay with this
// hook's own handling, as it did when webpack left the import alone (webpackIgnore).
// eslint-disable-next-line no-new-func
const importFromUrl = new Function('url', 'return import(url)')

/**
 * Dynamically load MediaPipe from CDN using ES module import.
 * This bypasses bundling entirely.
 */
const loadMediaPipeFromCDN = async () => {
  if (mediaPipeModulePromise) {
    return mediaPipeModulePromise
  }

  mediaPipeModulePromise = (async () => {
    const module = await importFromUrl(`${MEDIAPIPE_CDN_URL}/vision_bundle.mjs`)
    return module
  })()

  return mediaPipeModulePromise
}

/**
 * Hook for virtual background functionality in the DoctorCamera component.
 * Uses MediaPipe Tasks Vision ImageSegmenter for real-time portrait segmentation.
 *
 * @param {Object} options
 * @param {HTMLVideoElement} options.videoElement - The video element to process
 * @param {boolean} options.enabled - Whether the feature is enabled (FF check)
 * @param {string} options.scanId - For analytics tracking
 */
export default function useVirtualBackground({ videoElement, enabled, scanId }) {
  const [mode, setMode] = useState(VB_MODES.OFF)
  const [isLoading, setIsLoading] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)
  const [isSupported, setIsSupported] = useState(true)
  const [error, setError] = useState(null)

  const isMountedRef = useRef(true)
  const segmenterRef = useRef(null)
  const canvasRef = useRef(null)
  const offscreenCanvasRef = useRef(null)
  const initInProgressRef = useRef(false)
  const animationFrameRef = useRef(null)
  const lastFrameTimeRef = useRef(0)

  const frameInterval = 1000 / VB_TARGET_FPS

  // Initialize MediaPipe segmenter
  const initSegmenter = useCallback(async () => {
    if (segmenterRef.current || !enabled || !isSupported || initInProgressRef.current) return

    initInProgressRef.current = true
    setIsLoading(true)
    setError(null)

    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Segmenter initialization timeout')), VB_INIT_TIMEOUT_MS)
    )

    try {
      const initPromise = (async () => {
        const { ImageSegmenter, FilesetResolver } = await loadMediaPipeFromCDN()

        const vision = await FilesetResolver.forVisionTasks(WASM_CDN_URL)

        const preferredDelegate = isFirefox ? 'CPU' : 'GPU'

        const createSegmenter = async (delegate, modelAssetPath) =>
          ImageSegmenter.createFromOptions(vision, {
            baseOptions: {
              modelAssetPath,
              delegate
            },
            runningMode: 'VIDEO',
            outputCategoryMask: true
          })

        const createWithDelegateFallback = async modelAssetPath => {
          try {
            const segmenter = await createSegmenter(preferredDelegate, modelAssetPath)
            return { segmenter, delegate: preferredDelegate }
          } catch (delegateError) {
            if (preferredDelegate === 'GPU') {
              const segmenter = await createSegmenter('CPU', modelAssetPath)
              return { segmenter, delegate: 'CPU' }
            }
            throw delegateError
          }
        }

        const isModelAssetError = err => {
          const msg = err?.message ?? ''
          return (
            msg.includes('not a valid Flatbuffer buffer') ||
            msg.includes('The model is not a valid Flatbuffer') ||
            msg.includes('Failed to fetch model') ||
            msg.includes('Failed to fetch')
          )
        }

        try {
          const { segmenter, delegate } = await createWithDelegateFallback(getLocalModelUrl())
          return { segmenter, delegate, modelSource: 'local' }
        } catch (err) {
          if (!isModelAssetError(err)) {
            throw err
          }

          // Some hosting setups rewrite unknown extensions (like `.tflite`) to `/index.html`,
          // which causes the model fetch to return HTML and MediaPipe to error.
          // Fallback to the official MediaPipe model URL.
          const { segmenter, delegate } = await createWithDelegateFallback(FALLBACK_MODEL_URL)
          return { segmenter, delegate, modelSource: 'fallback' }
        }
      })()

      const { segmenter, delegate, modelSource } = await Promise.race([initPromise, timeoutPromise])
      segmenterRef.current = segmenter

      // Create offscreen canvas for processing
      offscreenCanvasRef.current = document.createElement('canvas')
      offscreenCanvasRef.current.width = VB_CANVAS_SIZE
      offscreenCanvasRef.current.height = VB_CANVAS_SIZE

      if (isMountedRef.current) {
        setIsInitialized(true)
      }

      trackEvent('ScanReview_VirtualBackground_Initialized', { scanId, delegate, modelSource })
    } catch (err) {
      log('Virtual background init failed', { scanId, error: err.message, stack: err.stack }, 'error')
      if (isMountedRef.current) {
        setError(err.message)
        setIsSupported(false)
        setIsInitialized(false)
      }
      trackEvent('ScanReview_VirtualBackground_InitFailed', { scanId, error: err.message })
    } finally {
      initInProgressRef.current = false
      if (isMountedRef.current) {
        setIsLoading(false)
      }
    }
  }, [enabled, scanId, isSupported])

  // Process a single frame
  const processFrame = useCallback(
    timestamp => {
      if (!segmenterRef.current || !videoElement || !canvasRef.current || mode === VB_MODES.OFF) {
        animationFrameRef.current = null
        return
      }

      if (videoElement.readyState < 2 || videoElement.videoWidth === 0 || videoElement.videoHeight === 0) {
        animationFrameRef.current = requestAnimationFrame(processFrame)
        return
      }

      // Throttle to target FPS
      if (timestamp - lastFrameTimeRef.current < frameInterval) {
        animationFrameRef.current = requestAnimationFrame(processFrame)
        return
      }
      lastFrameTimeRef.current = timestamp

      try {
        const canvas = canvasRef.current
        const ctx = canvas.getContext('2d')
        const offscreenCanvas = offscreenCanvasRef.current
        const offscreenCtx = offscreenCanvas.getContext('2d')

        // Draw video frame to offscreen canvas for processing
        drawVideoCover(offscreenCtx, videoElement, VB_CANVAS_SIZE, VB_CANVAS_SIZE)

        // Run segmentation
        const result = segmenterRef.current.segmentForVideo(offscreenCanvas, timestamp)
        const mask = result.categoryMask?.getAsUint8Array()

        if (!mask) {
          // Fallback: just draw the video
          drawVideoCover(ctx, videoElement, canvas.width, canvas.height)
          animationFrameRef.current = requestAnimationFrame(processFrame)
          return
        }

        // Get image data from offscreen canvas
        const imageData = offscreenCtx.getImageData(0, 0, VB_CANVAS_SIZE, VB_CANVAS_SIZE)
        const pixels = imageData.data

        // Create output canvas for compositing
        const outputImageData = ctx.createImageData(canvas.width, canvas.height)
        const outputPixels = outputImageData.data

        // Scale factor for mapping mask to output
        const scaleX = VB_CANVAS_SIZE / canvas.width
        const scaleY = VB_CANVAS_SIZE / canvas.height

        // Draw background first
        ctx.filter = `blur(${VB_BLUR_AMOUNT}px)`
        drawVideoCover(ctx, videoElement, canvas.width, canvas.height)
        ctx.filter = 'none'

        // Get background pixels
        const bgImageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const bgPixels = bgImageData.data

        // Infer which category represents background by sampling the corners of the mask.
        // This avoids relying on a specific category index (it varies by model).
        const topLeft = mask[0]
        const topRight = mask[VB_CANVAS_SIZE - 1]
        const bottomLeft = mask[(VB_CANVAS_SIZE - 1) * VB_CANVAS_SIZE]
        const bottomRight = mask[VB_CANVAS_SIZE * VB_CANVAS_SIZE - 1]

        let backgroundCategory = topLeft
        if (topRight === topLeft || bottomLeft === topLeft || bottomRight === topLeft) {
          backgroundCategory = topLeft
        } else if (bottomLeft === topRight || bottomRight === topRight) {
          backgroundCategory = topRight
        } else if (bottomRight === bottomLeft) {
          backgroundCategory = bottomLeft
        }

        // Composite: use mask to blend foreground (person) with background
        for (let y = 0; y < canvas.height; y++) {
          for (let x = 0; x < canvas.width; x++) {
            const outputIdx = (y * canvas.width + x) * 4

            // Map to mask coordinates
            const maskX = Math.floor(x * scaleX)
            const maskY = Math.floor(y * scaleY)
            const maskIdx = maskY * VB_CANVAS_SIZE + maskX

            // Category mask: treat anything that's not the background category as foreground.
            const isPerson = mask[maskIdx] !== backgroundCategory

            // Map to source video coordinates
            const srcX = Math.floor(x * scaleX)
            const srcY = Math.floor(y * scaleY)
            const srcIdx = (srcY * VB_CANVAS_SIZE + srcX) * 4

            if (isPerson) {
              // Use original video pixels (person)
              outputPixels[outputIdx] = pixels[srcIdx]
              outputPixels[outputIdx + 1] = pixels[srcIdx + 1]
              outputPixels[outputIdx + 2] = pixels[srcIdx + 2]
              outputPixels[outputIdx + 3] = 255
            } else {
              // Use background pixels
              outputPixels[outputIdx] = bgPixels[outputIdx]
              outputPixels[outputIdx + 1] = bgPixels[outputIdx + 1]
              outputPixels[outputIdx + 2] = bgPixels[outputIdx + 2]
              outputPixels[outputIdx + 3] = 255
            }
          }
        }

        ctx.putImageData(outputImageData, 0, 0)

        // Clean up mask
        result.categoryMask?.close()
      } catch (err) {
        // On error, just draw raw video
        const ctx = canvasRef.current?.getContext('2d')
        if (ctx && videoElement) {
          drawVideoCover(ctx, videoElement, canvasRef.current.width, canvasRef.current.height)
        }
      }

      animationFrameRef.current = requestAnimationFrame(processFrame)
    },
    [videoElement, mode, frameInterval]
  )

  // Handle mode change
  const handleModeChange = useCallback(
    (newMode, meta = {}) => {
      const prevMode = mode
      setMode(newMode)

      trackEvent('ScanReview_VirtualBackground_ModeChanged', {
        scanId,
        fromMode: prevMode,
        toMode: newMode,
        ...meta
      })
    },
    [mode, scanId]
  )

  // Start/stop processing based on mode
  useEffect(() => {
    const shouldRun = mode !== VB_MODES.OFF && isSupported && isInitialized && !!videoElement && !!canvasRef.current

    if (shouldRun && !animationFrameRef.current) {
      animationFrameRef.current = requestAnimationFrame(processFrame)
    }

    if (!shouldRun && animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
  }, [mode, isSupported, isInitialized, videoElement, processFrame])

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
      if (segmenterRef.current) {
        segmenterRef.current.close()
        segmenterRef.current = null
      }
    }
  }, [])

  return {
    mode,
    setMode: handleModeChange,
    isLoading,
    isInitialized,
    isSupported,
    error,
    canvasRef,
    initSegmenter,
    isActive: mode !== VB_MODES.OFF
  }
}
